/**
 * Meta Pixel (browser only).
 *
 * Conversions API (CAPI) must NOT run from the checkout JS bundle.
 * Access tokens belong on the server. After Stripe + Sticky.io confirm an
 * order, the backend should send Purchase with the same event_id as the
 * Pixel so Meta deduplicates to one conversion.
 *
 * Allowed Pixel custom_data: commerce fields only (value, currency,
 * content_ids, contents, num_items, order_id). Do not send quiz answers,
 * BMI, goal weight, medication preference, prescription status, or other
 * health information.
 */

export const META_PIXEL_ID =
  process.env.REACT_APP_META_PIXEL_ID || "3093456137523625";

const getCookie = (name) => {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp(
      `(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1")}=([^;]*)`
    )
  );
  return match ? decodeURIComponent(match[1]) : "";
};

const toNumber = (value) => {
  const n =
    typeof value === "string"
      ? parseFloat(value.replace(/,/g, ""))
      : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/** Product ids + prices only — no names or health attributes. */
const buildContents = (products = []) =>
  (products || []).map((p, index) => ({
    id: String(p.product_id || p.id || `product_${index}`),
    quantity: Number(p.quantity || 1),
    item_price: toNumber(p.product_price || p.price || 0),
  }));

const buildContentIds = (products = []) =>
  (products || [])
    .map((p, index) => String(p.product_id || p.id || `product_${index}`))
    .filter(Boolean);

/** Cookies + UA for backend CAPI matching (no health fields). */
export const getMetaBrowserContext = () => ({
  fbp: getCookie("_fbp") || "",
  fbc: getCookie("_fbc") || "",
  client_user_agent:
    typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
  event_source_url:
    typeof window !== "undefined" ? window.location.href || "" : "",
});

/** Shared Pixel + server CAPI event_id for Purchase deduplication. */
export const getMetaPurchaseEventId = (orderId) =>
  orderId ? `Purchase_${orderId}` : null;

const buildCommerceCustomData = ({
  value,
  currency = "USD",
  products = [],
  orderId,
}) => {
  const customData = {
    value: toNumber(value),
    currency,
    content_type: "product",
    content_ids: buildContentIds(products),
    contents: buildContents(products),
    num_items: Math.max(1, (products || []).length),
  };
  if (orderId) customData.order_id = String(orderId);
  return customData;
};

/**
 * @param {string} eventName
 * @param {object} customData
 * @param {string} eventId
 */
export const trackMetaPixel = (eventName, customData = {}, eventId) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") {
    return;
  }
  try {
    if (eventId) {
      window.fbq("track", eventName, customData, { eventID: eventId });
    } else {
      window.fbq("track", eventName, customData);
    }
  } catch (error) {
    console.error("Meta Pixel track error:", error);
  }
};

/**
 * Fire Meta Pixel only (no browser CAPI / Graph API).
 * Returns the event_id used so checkout can send it to the backend.
 */
export const trackMetaEvent = ({
  eventName,
  eventId,
  value,
  currency = "USD",
  products = [],
  orderId,
}) => {
  const id =
    eventId ||
    (orderId
      ? `${eventName}_${orderId}`
      : `${eventName}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

  const customData = buildCommerceCustomData({
    value,
    currency,
    products,
    orderId,
  });

  trackMetaPixel(eventName, customData, id);
  return id;
};

/** Fresh event_id per checkout start (timestamp + random). */
export const createUniqueMetaEventId = (eventName) =>
  `${eventName}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

export const trackMetaInitiateCheckout = (params = {}) =>
  trackMetaEvent({
    eventName: "InitiateCheckout",
    ...params,
    eventId: createUniqueMetaEventId("InitiateCheckout"),
  });

export const trackMetaAddPaymentInfo = (params = {}) =>
  trackMetaEvent({ eventName: "AddPaymentInfo", ...params });

/**
 * Browser Pixel Purchase. Backend must send CAPI Purchase with the same
 * event_id (Purchase_{orderId}) after Stripe + Sticky confirmation.
 */
export const trackMetaPurchase = (params = {}) => {
  const eventId =
    getMetaPurchaseEventId(params.orderId) || params.eventId;
  return trackMetaEvent({
    eventName: "Purchase",
    ...params,
    eventId,
  });
};
